import { type JSX, useState } from 'react';
import { generatePath } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Link } from 'Components/Buttons/Buttons';
import { OverflowMenu, OverflowMenuProps } from 'Components/Buttons/OverflowMenu';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_ADD_CREATE, ICON_DELETE_REMOVE, ICON_DOWNLOAD } from 'Config/Icons';
import { AI_DOC_GENERATOR_START } from 'Config/PathTemplates';
import { downloadDocument } from 'Helpers/FileUtils';
import { AiGeneratedDoc } from 'Models/ArtificialIntelligence';
import { FileState, UploadedFile } from 'Models/Files';
import { getHumanReadableGovernanceType } from 'Models/Governance';

import styles from './AiDocGeneratorTab.module.css';

enum Modal {
    Delete,
    None,
}

export interface AiDocGeneratorTabProps {
    aiDocGeneratorDelete: (aiGeneratedDoc: AiGeneratedDoc) => Promise<string>;
    allAiGeneratedDocs: AiGeneratedDoc[] | undefined;
    documentApi: DocumentApi;
    loadingErrorOccurred: boolean;
}

export const AiDocGeneratorTab = (props: AiDocGeneratorTabProps): JSX.Element => {
    const cachedData = useCachedData();

    const [displayedModal, setDisplayedModal] = useState(Modal.None);
    const [selectedAiGeneratedDoc, setSelectedAiGeneratedDoc] = useState<AiGeneratedDoc>();

    /** Download an AI-generated document. */
    const aiDocGeneratorDownload = async (file: UploadedFile): Promise<void> => {
        try {
            downloadDocument(props.documentApi, file);
        } catch (error) {
            console.error(error);
        }
    };

    /** Render an overflow menu for a given AI-generated document. */
    const getOverflowMenuProps = (aiGeneratedDoc: AiGeneratedDoc): OverflowMenuProps => {
        const overflowMenuItems = [];
        if (aiGeneratedDoc.input_file && aiGeneratedDoc.input_file.file_state === FileState.PASSED) {
            overflowMenuItems.push({
                text: 'Download Reference Document',
                onClickAction: () => aiDocGeneratorDownload(aiGeneratedDoc.input_file!),
                icon: ICON_DOWNLOAD,
            });
        }
        if (aiGeneratedDoc.output_file && aiGeneratedDoc.output_file.file_state === FileState.PASSED) {
            overflowMenuItems.push({
                text: 'Download Generated Document',
                onClickAction: () => aiDocGeneratorDownload(aiGeneratedDoc.output_file!),
                icon: ICON_DOWNLOAD,
            });
            overflowMenuItems.push({
                text: 'Delete Document',
                onClickAction: (): void => {
                    setDisplayedModal(Modal.Delete);
                    setSelectedAiGeneratedDoc(aiGeneratedDoc);
                },
                icon: ICON_DELETE_REMOVE,
            });
        }

        const overflowItemsProp: OverflowMenuProps = {
            overflowItems: overflowMenuItems,
            accessibilityTitle: `${aiGeneratedDoc.title} Menu`,
        };

        // If neither the input_file nor output_file are available for download yet, disable the overflow menu.
        if (overflowMenuItems.length === 0) {
            overflowItemsProp.disabled = true;
        }

        return overflowItemsProp;
    };

    const columns: ColumnConfig<AiGeneratedDoc>[] = [
        {
            type: ColumnType.CUSTOM,
            name: 'Title',
            getValue: (generatedDoc) => generatedDoc.title,
            sortComparator: (a, b) => a.localeCompare(b),
            getTableCellContent: (generatedDoc) => (
                <div className={styles.titleAndTypeContainer}>
                    {generatedDoc.output_file ? <UploadedFileAndState unclickable title={generatedDoc.title} documentApi={props.documentApi} file={generatedDoc.output_file} /> : <Text noStyles>{generatedDoc.title}</Text>}
                    <Text variant="Text3" noStyles color="darkGray">
                        {getHumanReadableGovernanceType(generatedDoc.type)}
                    </Text>
                </div>
            ),
        },
        {
            type: ColumnType.DATE,
            name: 'Created Timestamp',
            getDateString: (generatedDoc) => generatedDoc.created_timestamp,
        },
        {
            type: ColumnType.USER,
            name: 'Created By',
            getUserId: (generatedDoc) => generatedDoc.created_by,
            users: cachedData.users,
        },
        {
            type: ColumnType.STRING,
            name: 'Reference Document',
            getString: (generatedDoc) => generatedDoc.input_file?.filename,
        },
        {
            type: ColumnType.STRING,
            name: 'Regulation',
            getString: (generatedDoc) => generatedDoc.regulation?.name,
        },
        {
            type: ColumnType.STRING,
            name: 'Description',
            getString: (generatedDoc) => generatedDoc.description,
        },
    ];

    if (props.loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (!props.allAiGeneratedDocs) {
        return <Placeholder />;
    }

    return (
        <>
            {displayedModal === Modal.Delete && selectedAiGeneratedDoc && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.DELETE_WITHOUT_TYPING_TO_CONFIRM} headerText="Delete Document" areYouSureText="Are you sure you want to delete this document?" performOperation={() => props.aiDocGeneratorDelete(selectedAiGeneratedDoc)} hideModal={() => setDisplayedModal(Modal.None)}>
                    <Text>{selectedAiGeneratedDoc.title}</Text>
                </ConfirmationModal>
            )}
            <div className={styles.button}>
                <Link variant="primaryButton" to={generatePath(AI_DOC_GENERATOR_START)} fontAwesomeImage={ICON_ADD_CREATE}>
                    Generate New
                </Link>
            </div>
            <Table<AiGeneratedDoc> columns={columns} defaultSortColumn="Created Timestamp" defaultSortDirection="descending" filters={[]} getUniqueIdForRow={(generatedDoc) => generatedDoc.id} rowsData={props.allAiGeneratedDocs} getOverflowMenuForRow={(generatedDoc) => <OverflowMenu {...getOverflowMenuProps(generatedDoc)} />} />
        </>
    );
};
